import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CallbackWidget } from '@cian/callback-widget';

import { closeCallbackPopup } from '../../actions';
import { checkFsk, getContacts, getDeveloper, getUser, getUserPhone } from '../../selectors';
import { selectIsPopupOpen, selectPopupOpener, selectTimezone } from '../../selectors/callback';
import { trackSendCallbackRequest } from '../../services';
import { requestCallback } from '../../services/callbackRequest';
import { getWorkTimeSchedule } from '../../services/workTime';
import { ICallbackParams, TCallbackPlace } from '../../types/callback';
import { ApplicationContext, phoneToString } from '../../utils';
import { getPrivacyPolicyUrl, getSmsDisclaimerState } from './helpers';

export const CallbackPopupContainer = () => {
  const { config, httpApi, logger } = React.useContext(ApplicationContext);
  const privacyPolicyUrl = getPrivacyPolicyUrl(config);
  const isSMSDisclaimerEnabled = getSmsDisclaimerState(config);

  const isPopupOpen = useSelector(selectIsPopupOpen);
  const contacts = useSelector(getContacts);
  const developer = useSelector(getDeveloper);
  const user = useSelector(getUser);
  const userPhone = useSelector(getUserPhone);
  const isFsk = useSelector(checkFsk);
  const timezone = useSelector(selectTimezone);
  const opener = useSelector(selectPopupOpener);
  const dispatch = useDispatch();

  const preparedPhone = React.useMemo(() => phoneToString(contacts.phone), [contacts.phone]);

  const handlePopupClose = React.useCallback(() => dispatch(closeCallbackPopup()), [dispatch]);

  const handleRequestCallback = React.useCallback(
    async (parameters: ICallbackParams) => {
      const result = await requestCallback({ httpApi, logger }, parameters);
      trackSendCallbackRequest({ developer, parameters, user, place: opener as TCallbackPlace });

      return result;
    },
    [developer, opener, user, httpApi, logger],
  );

  const handleRequestWorkTime = React.useCallback(async () => {
    const userId = developer.realtyUserId;
    const result = await getWorkTimeSchedule({ httpApi, logger }, { userId });

    return result;
  }, [developer, httpApi, logger]);

  if (!preparedPhone) {
    return null;
  }

  return (
    <CallbackWidget
      agentPhone={preparedPhone}
      onClose={handlePopupClose}
      onRequestCallback={handleRequestCallback}
      onRequestWorkTime={handleRequestWorkTime}
      open={isPopupOpen}
      privacyPolicyUrl={privacyPolicyUrl}
      theme={isFsk ? 'fsk' : undefined}
      timezoneOffset={timezone}
      userPhoneNumber={userPhone}
      isSMSDisclaimerEnabled={isSMSDisclaimerEnabled}
    />
  );
};
